import {groupBy, orderBy, sumBy} from 'lodash';
import {PlainObject} from '@xh/hoist/core';
import {areaGradient, BENCHMARK_COLOR, CHART_DOWN_COLOR, CHART_FLAT_COLOR, CHART_UP_COLOR} from '../../../../cmp/ChartColors';
import {Position, RiskAnalytics, RiskPoint, TaxTreatmentEnum} from '../../../../data/DataTypes';

/**
 * Charts of the Performance widget's active metric, one per metric. Each one is drawn from the risk analytics' daily
 * points, holdings or trades, or from the live positions, so switching metrics needs no fetch - except CAGR, which
 * needs the whole history. Returns are charted in percent. Charts never animate.
 */
export type ChartKind =
    // performance
    | 'balance'
    | 'dailyPnl'
    | 'dailyReturns'
    | 'bestWorst'
    | 'alpha'
    | 'trades'
    | 'ytdPnl'
    | 'ytdGrowth'
    | 'ytdBalance'
    | 'ytdDva'
    | 'oneYear'
    | 'cagr'
    | 'flows'
    | 'dividends'
    | 'swr'
    | 'rmd'
    // risk
    | 'beta'
    | 'rollingCorrelation'
    | 'rollingVolatility'
    | 'rollingSharpe'
    | 'rollingSortino'
    | 'omega'
    | 'calendarYears'
    | 'drawdown'
    | 'rollingUlcer'
    | 'histogram'
    | 'tail'
    | 'qq'
    | 'capture'
    | 'activeReturn'
    | 'weights'
    | 'liquidity';

export interface ChartInputs {
    risk: RiskAnalytics;
    /** The whole history, for CAGR. Null until loaded. */
    allRisk?: RiskAnalytics;
    cagr?: number;
    /** Live positions in scope, for the dividends chart. */
    positions: Position[];
}

export interface PerformanceChart {
    config: PlainObject;
    series: PlainObject[];
    /** Series toggled from the chart's toolbar - set by {@link buildChart} in place of a legend. */
    toggles?: SeriesToggle[];
}

export interface SeriesToggle {
    id: string;
    name: string;
    color: string;
    visible: boolean;
    /** Button tooltip - the name, which the button may clip, with any figures. */
    tooltip?: string;
}

/** Trading days in the rolling windows - about a quarter. */
const ROLLING_DAYS = 63;
const TOP_N = 20;
/** Slice colors of the RMD donut, picked to stay apart on both themes. */
const PIE_COLORS = ['#58a6ff', '#a371f7', '#3fb950', '#d29922', '#f778ba', '#39c5cf', '#f0883e', '#6b7785'];

export function buildChart(kind: ChartKind, inputs: ChartInputs): PerformanceChart {
    const chart = buildChartUnstyled(kind, inputs);
    // Highcharts animates on first draw unless every level says not to
    chart.config = {...chart.config, chart: {...chart.config.chart, animation: false}};
    chart.config.plotOptions = {...chart.config.plotOptions, series: {...chart.config.plotOptions?.series, animation: false}};
    chart.series = chart.series.map((s, i) => ({...s, id: `s${i}`, animation: false}));

    // Series legends move to the toolbar as toggles. The RMD donut sets its slice toggles itself.
    if (chart.config.legend?.enabled) {
        chart.config.legend = {enabled: false};
        chart.toggles = chart.series.map(s => ({id: s.id, name: s.name, color: s.color, visible: true}));
    }
    return chart;
}

function buildChartUnstyled(kind: ChartKind, {risk, allRisk, cagr, positions}: ChartInputs): PerformanceChart {
    const points = risk?.points ?? [];
    switch (kind) {
        case 'balance':
            return balanceChart(points);
        case 'dailyPnl':
            return dailyPnlChart(points);
        case 'dailyReturns':
            return dailyReturnsChart(points);
        case 'bestWorst':
            return bestWorstChart(points);
        case 'alpha':
            return alphaChart(points);
        case 'trades':
            return tradesChart(risk);
        case 'ytdPnl':
            return ytdPnlChart(points);
        case 'ytdGrowth':
            return growthChart(sinceYearStart(points));
        case 'ytdBalance':
            return ytdBalanceChart(points);
        case 'ytdDva':
            return ytdDvaChart(points);
        case 'oneYear':
            return growthChart(lastYear(points));
        case 'cagr':
            return cagrChart(allRisk?.points ?? [], cagr);
        case 'flows':
            return flowsChart(points);
        case 'dividends':
            return dividendsChart(positions);
        case 'swr':
            return swrChart(points);
        case 'rmd':
            return rmdChart(positions);
        case 'beta':
            return betaChart(points);
        case 'rollingCorrelation':
            return rollingChart(points, 'correlation');
        case 'rollingVolatility':
            return rollingChart(points, 'volatility');
        case 'rollingSharpe':
            return rollingChart(points, 'sharpe');
        case 'rollingSortino':
            return rollingChart(points, 'sortino');
        case 'omega':
            return omegaChart(points);
        case 'calendarYears':
            return calendarYearsChart(points);
        case 'drawdown':
            return drawdownChart(points);
        case 'rollingUlcer':
            return rollingChart(points, 'ulcer');
        case 'histogram':
            return histogramChart(points, risk);
        case 'tail':
            return tailChart(points, risk);
        case 'qq':
            return qqChart(points);
        case 'capture':
            return captureChart(points);
        case 'activeReturn':
            return activeReturnChart(points);
        case 'weights':
            return weightsChart(risk);
        case 'liquidity':
            return liquidityChart(risk);
    }
}

//------------------------
// Performance
//------------------------
function balanceChart(points: RiskPoint[]): PerformanceChart {
    const color = dirColor(last(points)?.balance - points[0]?.balance);
    return {
        config: timeConfig({yFormat: compactMoney, tooltip: '${point.y:,.0f}'}),
        series: [
            {
                name: 'Balance',
                type: 'area',
                color,
                fillColor: areaGradient(color, '40'),
                lineWidth: 1.5,
                threshold: null,
                data: points.map(p => [ms(p.date), p.balance])
            }
        ]
    };
}

function dailyPnlChart(points: RiskPoint[]): PerformanceChart {
    return {
        config: timeConfig({yFormat: compactMoney, tooltip: '${point.y:,.0f}', zeroLine: true}),
        series: [
            {
                name: 'Daily P&L',
                type: 'column',
                color: CHART_UP_COLOR,
                negativeColor: CHART_DOWN_COLOR,
                borderWidth: 0,
                data: withReturns(points).map(({point, prev}) => [ms(point.date), point.balance - point.flow - prev.balance])
            }
        ]
    };
}

function dailyReturnsChart(points: RiskPoint[]): PerformanceChart {
    return {
        config: timeConfig({yFormat: pct, tooltip: '{point.y:.2f}%', zeroLine: true}),
        series: [returnColumns(withReturns(points).map(({point}) => point))]
    };
}

/** This year's daily returns, with the best and the worst day labeled. */
function bestWorstChart(points: RiskPoint[]): PerformanceChart {
    const days = withReturns(sinceYearStart(points)).map(({point}) => point),
        best = maxOf(days, p => p.return),
        worst = maxOf(days, p => -p.return),
        label = (p: RiskPoint, color: string) => ({
            x: ms(p.date),
            y: p.return * 100,
            color,
            dataLabels: {enabled: true, format: '{point.y:+.2f}%', style: {color, textOutline: 'none', fontWeight: 'bold'}}
        });
    const series = returnColumns(days);
    series.data = days.map(p => (p === best ? label(p, CHART_UP_COLOR) : p === worst ? label(p, CHART_DOWN_COLOR) : [ms(p.date), p.return * 100]));
    return {config: timeConfig({yFormat: pct, tooltip: '{point.y:.2f}%', zeroLine: true}), series: [series]};
}

/**
 * Jensen's alpha at each date, over the trailing quarter and annualized: the average of r - (Rf + beta x (Rm - Rf)),
 * with beta from the same quarter. Above zero the portfolio beat what its market exposure explains.
 */
function alphaChart(points: RiskPoint[]): PerformanceChart {
    const days = withReturns(points).map(({point}) => point),
        data = [];
    for (let i = ROLLING_DAYS - 1; i < days.length; i++) {
        const slice = days.slice(i - ROLLING_DAYS + 1, i + 1);
        data.push([ms(days[i].date), rollingAlpha(slice)]);
    }
    return {
        config: timeConfig({yFormat: pct, tooltip: '{point.y:.2f}%', zeroLine: true}),
        series: [
            {
                name: `Alpha (${ROLLING_DAYS}d, ann.)`,
                type: 'area',
                color: CHART_UP_COLOR,
                negativeColor: CHART_DOWN_COLOR,
                fillOpacity: 0.2,
                lineWidth: 1.5,
                threshold: 0,
                data
            }
        ]
    };
}

function rollingAlpha(slice: RiskPoint[]): number {
    const rp = slice.map(p => p.return),
        rb = slice.map(p => p.benchmarkReturn),
        vb = variance(rb);
    if (!vb) return null;
    const beta = covariance(rp, rb) / vb,
        alpha = mean(slice.map(p => p.return - ((p.riskFree ?? 0) + beta * (p.benchmarkReturn - (p.riskFree ?? 0)))));
    return alpha * 252 * 100;
}

function tradesChart(risk: RiskAnalytics): PerformanceChart {
    const trades = risk?.trades ?? [];
    return {
        config: timeConfig({yFormat: compactMoney, tooltip: '${point.y:,.0f}', legend: true, zeroLine: true, monthly: true}),
        series: [
            {name: 'Buys', type: 'column', color: CHART_UP_COLOR, borderWidth: 0, data: trades.map(t => [ms(t.month), t.buys])},
            {name: 'Sells', type: 'column', color: CHART_DOWN_COLOR, borderWidth: 0, data: trades.map(t => [ms(t.month), -t.sells])}
        ]
    };
}

/** Dollar P&L since the start of the year, net of deposits. */
function ytdPnlChart(points: RiskPoint[]): PerformanceChart {
    const span = sinceYearStart(points);
    let total = 0;
    const data = span.map((p, i) => [ms(p.date), i === 0 ? 0 : (total += p.balance - p.flow - span[i - 1].balance)]),
        color = dirColor(total);
    return {
        config: timeConfig({yFormat: compactMoney, tooltip: '${point.y:,.0f}', zeroLine: true}),
        series: [{name: 'YTD P&L', type: 'area', color, negativeColor: CHART_DOWN_COLOR, fillOpacity: 0.15, lineWidth: 1.5, threshold: 0, data}]
    };
}

/** The balance since the start of the year, against the starting balance plus what was deposited since. */
function ytdBalanceChart(points: RiskPoint[]): PerformanceChart {
    const span = sinceYearStart(points),
        start = span[0]?.balance ?? 0;
    let deposits = 0;
    const funded = span.map((p, i) => [ms(p.date), start + (i === 0 ? 0 : (deposits += p.flow))]);
    return {
        config: timeConfig({yFormat: compactMoney, tooltip: '${point.y:,.0f}', legend: true}),
        series: [
            {
                name: 'Balance',
                type: 'area',
                color: CHART_UP_COLOR,
                fillColor: areaGradient(CHART_UP_COLOR, '30'),
                lineWidth: 1.5,
                threshold: null,
                data: span.map(p => [ms(p.date), p.balance])
            },
            {name: 'Start + deposits', type: 'line', step: 'left', color: CHART_FLAT_COLOR, dashStyle: 'ShortDash', data: funded}
        ]
    };
}

/** Growth since the start of the year with the deposits counted in, against SPX. */
function ytdDvaChart(points: RiskPoint[]): PerformanceChart {
    const span = sinceYearStart(points),
        start = span[0]?.balance,
        dva = start ? span.map(p => [ms(p.date), (p.balance / start - 1) * 100]) : [];
    return {
        config: timeConfig({yFormat: pct, tooltip: '{point.y:.2f}%', legend: true, zeroLine: true}),
        series: [
            {name: 'Portfolio, deposits included', type: 'line', color: dirColor(last(dva)?.[1]), lineWidth: 1.5, data: dva},
            {name: 'SPX', type: 'line', color: BENCHMARK_COLOR, dashStyle: 'ShortDot', lineWidth: 1.5, data: compoundSeries(span, p => p.benchmarkReturn)}
        ]
    };
}

/** Compounded return of the portfolio and of SPX over the points. */
function growthChart(points: RiskPoint[]): PerformanceChart {
    const portfolio = compoundSeries(points, p => p.return),
        benchmark = compoundSeries(points, p => p.benchmarkReturn);
    return {
        config: timeConfig({yFormat: pct, tooltip: '{point.y:.2f}%', legend: true, zeroLine: true}),
        series: [
            {name: 'Portfolio', type: 'line', color: dirColor(last(portfolio)?.[1]), lineWidth: 1.5, data: portfolio},
            {name: 'SPX', type: 'line', color: BENCHMARK_COLOR, dashStyle: 'ShortDot', lineWidth: 1.5, data: benchmark}
        ]
    };
}

/**
 * The balance since the first one, against the smooth path CAGR describes - the same start, growing at the CAGR. CAGR
 * counts deposits as growth, so both end near today's balance. Log scale, so steady growth is a straight line.
 */
function cagrChart(points: RiskPoint[], cagr: number): PerformanceChart {
    const first = points[0],
        path = first && cagr != null ? points.map(p => [ms(p.date), first.balance * Math.pow(1 + cagr, (ms(p.date) - ms(first.date)) / (365 * 86400000))]) : [];
    const config = timeConfig({yFormat: compactMoney, tooltip: '${point.y:,.0f}', legend: true});
    config.yAxis = {...config.yAxis, type: 'logarithmic'};
    return {
        config,
        series: [
            {name: 'Balance', type: 'line', color: CHART_UP_COLOR, lineWidth: 1.5, data: points.filter(p => p.balance > 0).map(p => [ms(p.date), p.balance])},
            {
                name: cagr != null ? `CAGR ${(cagr * 100).toFixed(1)}%` : 'CAGR',
                type: 'line',
                color: BENCHMARK_COLOR,
                dashStyle: 'ShortDash',
                lineWidth: 1.5,
                data: path
            }
        ]
    };
}

function flowsChart(points: RiskPoint[]): PerformanceChart {
    const span = sinceYearStart(points);
    let total = 0;
    return {
        config: timeConfig({yFormat: compactMoney, tooltip: '${point.y:,.0f}', legend: true, zeroLine: true}),
        series: [
            {
                name: 'Deposit',
                type: 'column',
                color: CHART_UP_COLOR,
                negativeColor: CHART_DOWN_COLOR,
                borderWidth: 0,
                data: span.filter(p => p.flow).map(p => [ms(p.date), p.flow])
            },
            {
                name: 'Year to date',
                type: 'line',
                step: 'left',
                color: CHART_FLAT_COLOR,
                dashStyle: 'ShortDash',
                data: span.map((p, i) => [ms(p.date), i === 0 ? 0 : (total += p.flow)])
            }
        ]
    };
}

/** What 4% of the balance would have paid out over the window - the withdrawal the SWR row assumes. */
function swrChart(points: RiskPoint[]): PerformanceChart {
    return {
        config: timeConfig({yFormat: compactMoney, tooltip: '${point.y:,.0f}'}),
        series: [
            {
                name: '4% of balance',
                type: 'area',
                color: BENCHMARK_COLOR,
                fillColor: areaGradient(BENCHMARK_COLOR, '30'),
                lineWidth: 1.5,
                threshold: null,
                data: points.map(p => [ms(p.date), p.balance * 0.04])
            }
        ]
    };
}

/** The RMD estimate - 4% of the market value - of each tax-deferred portfolio, the only ones that count toward it. */
function rmdChart(positions: Position[]): PerformanceChart {
    const portfolios = orderBy(
        Object.values(
            groupBy(
                positions.filter(p => p.portfolioTaxType == TaxTreatmentEnum.TaxDeferred),
                'portfolioId'
            )
        ).map(ps => ({name: ps[0].portfolioName, rmd: sumBy(ps, 'marketValue') * 0.04})),
        'rmd',
        'desc'
    );
    const total = sumBy(portfolios, 'rmd'),
        money = (v: number) => `$${Math.round(v).toLocaleString()}`,
        slices = portfolios.map((p, i) => ({id: `p${i}`, name: p.name, y: p.rmd, color: PIE_COLORS[i % PIE_COLORS.length]}));
    // Portfolios are toggled from the toolbar, the total sits in the hole - labels and a legend both shrank the ring
    return {
        config: {
            ...baseConfig(false),
            chart: {type: 'pie', spacing: [4, 4, 4, 4]},
            title: {
                text: `<div style="text-align:center;color:var(--xh-text-color)"><div style="font-size:10px;opacity:0.7">TOTAL</div><b>${money(total)}</b></div>`,
                useHTML: true,
                floating: true,
                verticalAlign: 'middle',
                style: {fontSize: '13px'}
            },
            tooltip: {pointFormat: '<b>${point.y:,.0f}</b> ({point.percentage:.1f}%)'},
            plotOptions: {
                pie: {
                    size: '100%',
                    innerSize: '55%',
                    borderWidth: 1,
                    borderColor: 'rgba(0, 0, 0, 0.35)',
                    dataLabels: {
                        enabled: true,
                        distance: '-22%',
                        format: '{point.percentage:.0f}%',
                        filter: {property: 'percentage', operator: '>', value: 4},
                        style: {fontSize: '11px', fontWeight: 'bold', color: '#ffffff', textOutline: '1px rgba(0, 0, 0, 0.5)'}
                    }
                }
            }
        },
        series: [{name: 'RMD', type: 'pie', data: slices}],
        toggles: slices.map(sl => ({
            id: sl.id,
            name: sl.name,
            color: sl.color,
            visible: true,
            tooltip: `${sl.name}: ${money(sl.y)} (${total ? ((sl.y / total) * 100).toFixed(0) : 0}%)`
        }))
    };
}

function dividendsChart(positions: Position[]): PerformanceChart {
    const byTicker = Object.entries(groupBy(positions, 'ticker')).map(([ticker, ps]) => ({ticker, dividend: sumBy(ps, 'lastDividend')})),
        top = takeTop(
            byTicker.filter(d => d.dividend > 0),
            d => d.dividend
        );
    return {
        config: categoryConfig(
            top.map(d => d.ticker),
            compactMoney,
            '${point.y:,.0f}'
        ),
        series: [{name: 'Last dividend', type: 'column', color: CHART_UP_COLOR, borderWidth: 0, data: top.map(d => d.dividend)}]
    };
}

//------------------------
// Risk
//------------------------
/** Daily portfolio return against the SPX return, with the regression line - its slope is beta. */
function betaChart(points: RiskPoint[]): PerformanceChart {
    const days = withReturns(points).map(({point}) => point),
        rb = days.map(p => p.benchmarkReturn),
        rp = days.map(p => p.return),
        beta = variance(rb) ? covariance(rp, rb) / variance(rb) : null,
        intercept = beta != null ? mean(rp) - beta * mean(rb) : null,
        minX = Math.min(...rb),
        maxX = Math.max(...rb);
    return {
        config: {
            ...baseConfig(true),
            chart: {type: 'scatter', zoomType: 'xy'},
            xAxis: {title: {text: 'SPX daily %'}, labels: {format: '{value}%'}, gridLineWidth: 1, gridLineDashStyle: 'Dot', plotLines: [zeroLine()]},
            yAxis: {title: {text: 'Portfolio daily %'}, labels: {format: '{value}%'}, gridLineDashStyle: 'Dot', plotLines: [zeroLine()]},
            tooltip: {pointFormat: 'SPX {point.x:.2f}% · Portfolio {point.y:.2f}%'}
        },
        series: [
            {
                name: 'Days',
                type: 'scatter',
                color: `${CHART_UP_COLOR}99`,
                marker: {radius: 2, symbol: 'circle'},
                data: days.map(p => [p.benchmarkReturn * 100, p.return * 100])
            },
            ...(beta != null
                ? [
                      {
                          name: `β ${beta.toFixed(2)}`,
                          type: 'line',
                          color: BENCHMARK_COLOR,
                          marker: {enabled: false},
                          enableMouseTracking: false,
                          data: [
                              [minX * 100, (intercept + beta * minX) * 100],
                              [maxX * 100, (intercept + beta * maxX) * 100]
                          ]
                      }
                  ]
                : [])
        ]
    };
}

type RollingMetric = 'correlation' | 'volatility' | 'sharpe' | 'sortino' | 'ulcer';

/** A metric over a rolling quarter. Volatility, Sharpe and Sortino also for SPX, as a reference. */
function rollingChart(points: RiskPoint[], metric: RollingMetric): PerformanceChart {
    const days = withReturns(points).map(({point}) => point),
        portfolio = [],
        benchmark = [],
        withBenchmark = metric !== 'correlation' && metric !== 'ulcer';
    for (let i = ROLLING_DAYS - 1; i < days.length; i++) {
        const slice = days.slice(i - ROLLING_DAYS + 1, i + 1),
            x = ms(days[i].date);
        portfolio.push([x, rollingValue(slice, p => p.return, metric)]);
        if (withBenchmark) benchmark.push([x, rollingValue(slice, p => p.benchmarkReturn, metric)]);
    }
    const isPct = metric === 'volatility';
    return {
        config: timeConfig({
            yFormat: isPct ? pct : num,
            tooltip: isPct ? '{point.y:.1f}%' : '{point.y:.2f}',
            legend: withBenchmark,
            zeroLine: metric !== 'volatility' && metric !== 'ulcer'
        }),
        series: [
            {
                name: `Portfolio (${ROLLING_DAYS}d)`,
                type: 'line',
                color: metric === 'ulcer' ? CHART_DOWN_COLOR : CHART_UP_COLOR,
                lineWidth: 1.5,
                data: portfolio
            },
            ...(withBenchmark
                ? [{name: `SPX (${ROLLING_DAYS}d)`, type: 'line', color: BENCHMARK_COLOR, dashStyle: 'ShortDot', lineWidth: 1.5, data: benchmark}]
                : [])
        ]
    };
}

function rollingValue(slice: RiskPoint[], fn: (p: RiskPoint) => number, metric: RollingMetric): number {
    const returns = slice.map(fn),
        sd = stdev(returns),
        annual = Math.sqrt(252);
    switch (metric) {
        case 'correlation': {
            const rb = slice.map(p => p.benchmarkReturn),
                sb = stdev(rb);
            return sd && sb ? covariance(returns, rb) / (sd * sb) : null;
        }
        case 'volatility':
            return sd * annual * 100;
        case 'ulcer': {
            const dd = drawdowns(returns);
            return Math.sqrt(mean(dd.map(d => d * 100 * (d * 100))));
        }
        default: {
            if (slice.some(p => p.riskFree == null)) return null;
            const excess = slice.map(p => fn(p) - p.riskFree),
                risk = metric === 'sharpe' ? sd : Math.sqrt(mean(excess.map(e => Math.min(e, 0) ** 2)));
            return risk ? (mean(excess) / risk) * annual : null;
        }
    }
}

/** Running sums of the daily gains and of the daily losses - Omega is the first over the second. */
function omegaChart(points: RiskPoint[]): PerformanceChart {
    let gains = 0,
        losses = 0;
    const days = withReturns(points).map(({point}) => point),
        gainData = days.map(p => [ms(p.date), (gains += Math.max(p.return, 0)) * 100]),
        lossData = days.map(p => [ms(p.date), (losses += Math.max(-p.return, 0)) * 100]);
    return {
        config: timeConfig({yFormat: pct, tooltip: '{point.y:.1f}%', legend: true}),
        series: [
            {name: 'Gains', type: 'line', color: CHART_UP_COLOR, lineWidth: 1.5, data: gainData},
            {name: 'Losses', type: 'line', color: CHART_DOWN_COLOR, lineWidth: 1.5, data: lossData}
        ]
    };
}

/** Each calendar year's return beside its deepest drawdown - Calmar is the one over the other. */
function calendarYearsChart(points: RiskPoint[]): PerformanceChart {
    const years = Object.entries(groupBy(withReturns(points), ({point}) => point.date.slice(0, 4))).map(([year, days]) => {
        const returns = days.map(({point}) => point.return);
        return {year, ret: compound(returns) * 100, maxDd: Math.min(...drawdowns(returns)) * 100};
    });
    return {
        config: {
            ...categoryConfig(
                years.map(y => y.year),
                pct,
                '{point.y:.1f}%'
            ),
            legend: legendConfig(true)
        },
        series: [
            {name: 'Return', type: 'column', color: CHART_UP_COLOR, negativeColor: CHART_DOWN_COLOR, borderWidth: 0, data: years.map(y => y.ret)},
            {name: 'Max drawdown', type: 'column', color: `${CHART_DOWN_COLOR}80`, borderWidth: 0, data: years.map(y => y.maxDd)}
        ]
    };
}

/** Drawdown below the running peak of the compounded returns, for the portfolio and for SPX. */
function drawdownChart(points: RiskPoint[]): PerformanceChart {
    return {
        config: timeConfig({yFormat: pct, tooltip: '{point.y:.2f}%', legend: true}),
        series: [
            {
                name: 'Portfolio',
                type: 'area',
                color: CHART_DOWN_COLOR,
                fillColor: areaGradient(CHART_DOWN_COLOR, '50'),
                lineWidth: 1,
                threshold: 0,
                data: drawdownSeries(points, p => p.return)
            },
            {name: 'SPX', type: 'line', color: BENCHMARK_COLOR, dashStyle: 'ShortDot', lineWidth: 1.5, data: drawdownSeries(points, p => p.benchmarkReturn)}
        ]
    };
}

/** How often each size of daily return happened, with the 95% VaR marked. */
function histogramChart(points: RiskPoint[], risk: RiskAnalytics): PerformanceChart {
    const returns = withReturns(points).map(({point}) => point.return * 100),
        bins = 40,
        lo = Math.min(...returns),
        hi = Math.max(...returns),
        width = (hi - lo) / bins || 1,
        counts = new Array(bins).fill(0);
    returns.forEach(r => counts[Math.min(bins - 1, Math.floor((r - lo) / width))]++);

    return {
        config: {
            ...baseConfig(false),
            chart: {type: 'column'},
            xAxis: {
                title: {text: 'Daily return %'},
                labels: {format: '{value}%'},
                plotLines:
                    risk?.var95 != null
                        ? [
                              {
                                  value: risk.var95 * 100,
                                  color: '#f0b429',
                                  width: 1.5,
                                  dashStyle: 'Dash',
                                  zIndex: 5,
                                  label: {text: 'VaR 95%', style: {color: '#f0b429'}, rotation: 0, y: 12}
                              }
                          ]
                        : []
            },
            yAxis: {title: {text: 'Days'}, gridLineDashStyle: 'Dot'},
            tooltip: {pointFormat: '{point.y} days around {point.x:.2f}%'},
            plotOptions: {column: {pointPadding: 0, groupPadding: 0, borderWidth: 0}}
        },
        series: [
            {
                name: 'Days',
                // losses red, gains green
                zones: [{value: 0, color: CHART_DOWN_COLOR}, {color: CHART_UP_COLOR}],
                zoneAxis: 'x',
                data: counts.map((c, i) => [lo + width * (i + 0.5), c])
            }
        ]
    };
}

/** The worst 5% of days, worst first - CVaR is their average, VaR the best of them. */
function tailChart(points: RiskPoint[], risk: RiskAnalytics): PerformanceChart {
    const days = withReturns(points).map(({point}) => point),
        tail = orderBy(days, p => p.return).slice(0, Math.max(1, Math.ceil(days.length * 0.05))),
        line = (value: number, text: string, color: string) =>
            value != null ? {value: value * 100, color, width: 1.5, dashStyle: 'Dash', zIndex: 5, label: {text, align: 'right', style: {color}}} : null;
    return {
        config: {
            ...categoryConfig(
                tail.map(p => p.date.slice(0, 10)),
                pct,
                '{point.y:.2f}%'
            ),
            yAxis: {
                title: {text: null},
                opposite: true,
                gridLineDashStyle: 'Dot',
                labels: {formatter: pct},
                plotLines: [line(risk?.var95, 'VaR 95%', '#f0b429'), line(risk?.cvar95, 'CVaR 95%', CHART_DOWN_COLOR)].filter(Boolean)
            }
        },
        series: [{name: 'Daily return', type: 'column', color: CHART_DOWN_COLOR, borderWidth: 0, data: tail.map(p => p.return * 100)}]
    };
}

/**
 * Quantile-quantile plot of the daily returns against a normal distribution with the same mean and volatility. Points
 * on the line are normal; points below it on the left are a fatter loss tail (negative skew, excess kurtosis).
 */
function qqChart(points: RiskPoint[]): PerformanceChart {
    const returns = withReturns(points)
            .map(({point}) => point.return)
            .sort((a, b) => a - b),
        n = returns.length,
        m = mean(returns),
        sd = stdev(returns),
        data = returns.map((r, i) => [(m + sd * inverseNormal((i + 0.5) / n)) * 100, r * 100]),
        ends = data.length ? [Math.min(data[0][0], data[0][1]), Math.max(data[n - 1][0], data[n - 1][1])] : [];
    return {
        config: {
            ...baseConfig(true),
            chart: {type: 'scatter', zoomType: 'xy'},
            xAxis: {title: {text: 'Normal %'}, labels: {format: '{value}%'}, gridLineWidth: 1, gridLineDashStyle: 'Dot'},
            yAxis: {title: {text: 'Actual %'}, labels: {format: '{value}%'}, gridLineDashStyle: 'Dot'},
            tooltip: {pointFormat: 'Normal {point.x:.2f}% · Actual {point.y:.2f}%'}
        },
        series: [
            {name: 'Daily returns', type: 'scatter', color: `${CHART_UP_COLOR}99`, marker: {radius: 2, symbol: 'circle'}, data},
            {
                name: 'Normal',
                type: 'line',
                color: BENCHMARK_COLOR,
                dashStyle: 'ShortDash',
                marker: {enabled: false},
                enableMouseTracking: false,
                data: ends.map(v => [v, v])
            }
        ]
    };
}

/** Average daily return on SPX's up days and on its down days, for the portfolio and for SPX itself. */
function captureChart(points: RiskPoint[]): PerformanceChart {
    const days = withReturns(points).map(({point}) => point),
        up = days.filter(p => p.benchmarkReturn > 0),
        down = days.filter(p => p.benchmarkReturn < 0),
        avg = (ps: RiskPoint[], fn: (p: RiskPoint) => number) => (ps.length ? mean(ps.map(fn)) * 100 : null);
    return {
        config: {
            ...categoryConfig(['SPX up days', 'SPX down days'], pct, '{point.y:.3f}%'),
            legend: legendConfig(true),
            yAxis: {title: {text: null}, opposite: true, gridLineDashStyle: 'Dot', labels: {formatter: pct}, plotLines: [zeroLine()]}
        },
        series: [
            {
                name: 'Portfolio',
                type: 'column',
                color: CHART_UP_COLOR,
                negativeColor: CHART_DOWN_COLOR,
                borderWidth: 0,
                data: [avg(up, p => p.return), avg(down, p => p.return)]
            },
            {name: 'SPX', type: 'column', color: BENCHMARK_COLOR, borderWidth: 0, data: [avg(up, p => p.benchmarkReturn), avg(down, p => p.benchmarkReturn)]}
        ]
    };
}

/** Portfolio return less the SPX return, both compounded from the start of the window. */
function activeReturnChart(points: RiskPoint[]): PerformanceChart {
    const portfolio = compoundSeries(points, p => p.return),
        benchmark = compoundSeries(points, p => p.benchmarkReturn),
        data = portfolio.map(([x, y], i) => [x, ((1 + y / 100) / (1 + benchmark[i][1] / 100) - 1) * 100]);
    return {
        config: timeConfig({yFormat: pct, tooltip: '{point.y:.2f}%', zeroLine: true}),
        series: [
            {name: 'Ahead of SPX', type: 'area', color: CHART_UP_COLOR, negativeColor: CHART_DOWN_COLOR, fillOpacity: 0.15, lineWidth: 1.5, threshold: 0, data}
        ]
    };
}

/** The largest holdings by weight, with the running total - how much of the book the top few make up. */
function weightsChart(risk: RiskAnalytics): PerformanceChart {
    const top = takeTop(risk?.holdings ?? [], h => h.weight);
    let total = 0;
    return {
        config: categoryConfig(
            top.map(h => h.ticker),
            pct,
            '{point.y:.1f}%'
        ),
        series: [
            {name: 'Weight', type: 'column', color: BENCHMARK_COLOR, borderWidth: 0, data: top.map(h => h.weight * 100)},
            {name: 'Cumulative', type: 'line', color: CHART_FLAT_COLOR, marker: {radius: 2}, data: top.map(h => (total += h.weight) * 100)}
        ]
    };
}

function liquidityChart(risk: RiskAnalytics): PerformanceChart {
    const top = takeTop(
        (risk?.holdings ?? []).filter(h => h.daysToLiquidate != null),
        h => h.daysToLiquidate
    );
    return {
        config: categoryConfig(
            top.map(h => h.ticker),
            num,
            '{point.y:.2f} days'
        ),
        series: [{name: 'Days to liquidate', type: 'column', color: '#f0b429', borderWidth: 0, data: top.map(h => h.daysToLiquidate)}]
    };
}

//------------------------
// Config
//------------------------
interface TimeConfigOpts {
    yFormat: (this: {value: number}) => string;
    tooltip: string;
    legend?: boolean;
    zeroLine?: boolean;
    /** Monthly columns - buys above the axis and sells below share a bar. */
    monthly?: boolean;
}

function baseConfig(legend: boolean): PlainObject {
    return {
        title: {text: null},
        credits: {enabled: false},
        exporting: {enabled: false},
        legend: legendConfig(legend)
    };
}

function legendConfig(enabled: boolean): PlainObject {
    return {enabled, align: 'left', verticalAlign: 'top', itemStyle: {fontSize: '11px', fontWeight: 'normal'}};
}

function timeConfig({yFormat, tooltip, legend = false, zeroLine: zero, monthly}: TimeConfigOpts): PlainObject {
    return {
        ...baseConfig(legend),
        chart: {zoomType: 'x'},
        xAxis: {type: 'datetime'},
        yAxis: {title: {text: null}, opposite: true, gridLineDashStyle: 'Dot', labels: {formatter: yFormat}, plotLines: zero ? [zeroLine()] : []},
        tooltip: {
            shared: true,
            xDateFormat: monthly ? '%b %Y' : '%a %b %e, %Y',
            pointFormat: `<span style="color:{series.color}">●</span> {series.name}: <b>${tooltip}</b><br/>`
        },
        plotOptions: {series: {marker: {enabled: false}}, column: {grouping: !monthly, borderRadius: 1}}
    };
}

function categoryConfig(categories: string[], yFormat: (this: {value: number}) => string, tooltip: string): PlainObject {
    return {
        ...baseConfig(false),
        chart: {},
        xAxis: {categories, labels: {style: {fontSize: '10px'}}},
        yAxis: {title: {text: null}, opposite: true, gridLineDashStyle: 'Dot', labels: {formatter: yFormat}},
        tooltip: {shared: true, pointFormat: `<span style="color:{series.color}">●</span> {series.name}: <b>${tooltip}</b><br/>`}
    };
}

function returnColumns(days: RiskPoint[]): PlainObject {
    return {
        name: 'Daily return',
        type: 'column',
        color: CHART_UP_COLOR,
        negativeColor: CHART_DOWN_COLOR,
        borderWidth: 0,
        data: days.map(p => [ms(p.date), p.return * 100])
    };
}

function zeroLine() {
    return {value: 0, width: 1, color: 'rgba(128, 128, 128, 0.6)', zIndex: 3};
}

function compactMoney(this: {value: number}): string {
    const v = this.value,
        a = Math.abs(v);
    return a >= 1e9 ? `${(v / 1e9).toFixed(1)}b` : a >= 1e6 ? `${(v / 1e6).toFixed(1)}m` : a >= 1e3 ? `${(v / 1e3).toFixed(0)}k` : `${v}`;
}

function pct(this: {value: number}): string {
    return `${this.value}%`;
}

function num(this: {value: number}): string {
    return `${this.value}`;
}

//------------------------
// Points
//------------------------
function ms(date: string): number {
    return Date.parse(`${date.slice(0, 10)}T00:00:00Z`);
}

function last<T>(items: T[]): T {
    return items[items.length - 1];
}

/** Points from the last close of the previous year, so the first return is the year's first. */
function sinceYearStart(points: RiskPoint[]): RiskPoint[] {
    const year = last(points)?.date.slice(0, 4),
        first = points.findIndex(p => p.date.startsWith(year));
    return first < 0 ? [] : points.slice(Math.max(0, first - 1));
}

/** Points over the year to the last one, starting from the close a year before it. */
function lastYear(points: RiskPoint[]): RiskPoint[] {
    const end = last(points);
    if (!end) return [];
    const yearAgo = ms(end.date) - 365 * 86400000,
        first = points.findIndex(p => ms(p.date) > yearAgo);
    return points.slice(Math.max(0, first - 1));
}

/** Points that have a return, with the point before each. */
function withReturns(points: RiskPoint[]): {point: RiskPoint; prev: RiskPoint}[] {
    return points.map((point, i) => ({point, prev: points[i - 1]})).filter(({point, prev}) => prev && point.return != null && point.benchmarkReturn != null);
}

/** Returns compounded from the first point, in percent. The first point is 0. */
function compoundSeries(points: RiskPoint[], fn: (p: RiskPoint) => number): number[][] {
    let value = 1;
    return points.map((p, i) => {
        const r = i > 0 ? fn(p) : null;
        if (r != null) value *= 1 + r;
        return [ms(p.date), (value - 1) * 100];
    });
}

function drawdownSeries(points: RiskPoint[], fn: (p: RiskPoint) => number): number[][] {
    let value = 1,
        peak = 1;
    return points.map((p, i) => {
        const r = i > 0 ? fn(p) : null;
        if (r != null) value *= 1 + r;
        peak = Math.max(peak, value);
        return [ms(p.date), (value / peak - 1) * 100];
    });
}

//------------------------
// Math
//------------------------
function mean(x: number[]): number {
    return x.length ? sumBy(x) / x.length : null;
}

function covariance(x: number[], y: number[]): number {
    if (x.length < 2) return null;
    const mx = mean(x),
        my = mean(y);
    let sum = 0;
    for (let i = 0; i < x.length; i++) sum += (x[i] - mx) * (y[i] - my);
    return sum / (x.length - 1);
}

function variance(x: number[]): number {
    return covariance(x, x);
}

function stdev(x: number[]): number {
    const v = variance(x);
    return v != null ? Math.sqrt(v) : null;
}

function compound(returns: number[]): number {
    return returns.reduce((v, r) => v * (1 + r), 1) - 1;
}

/** Drawdown after each return below the running peak, starting from a peak. */
function drawdowns(returns: number[]): number[] {
    let value = 1,
        peak = 1;
    return returns.map(r => {
        value *= 1 + r;
        peak = Math.max(peak, value);
        return value / peak - 1;
    });
}

function maxOf<T>(items: T[], fn: (t: T) => number): T {
    return items.reduce((best, t) => (best == null || fn(t) > fn(best) ? t : best), null as T);
}

function takeTop<T>(items: T[], fn: (t: T) => number): T[] {
    return orderBy(items, fn, 'desc').slice(0, TOP_N);
}

function dirColor(v: number): string {
    return v < 0 ? CHART_DOWN_COLOR : CHART_UP_COLOR;
}

/** Inverse of the standard normal distribution - Acklam's rational approximation, accurate to about 1e-9. */
function inverseNormal(p: number): number {
    const a = [-39.69683028665376, 220.9460984245205, -275.9285104469687, 138.357751867269, -30.66479806614716, 2.506628274631],
        b = [-54.47609879822406, 161.5858368580409, -155.6989798598866, 66.80131188771972, -13.28068155288572],
        c = [-0.007784894002430293, -0.3223964580411365, -2.400758277161838, -2.549732539343734, 4.374664141464968, 2.938163982698783],
        d = [0.007784695709041462, 0.3224671290700398, 2.445134137142996, 3.754408661907416],
        low = 0.02425;
    if (p < low) {
        const q = Math.sqrt(-2 * Math.log(p));
        return (((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) / ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1);
    }
    if (p > 1 - low) {
        const q = Math.sqrt(-2 * Math.log(1 - p));
        return -(((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) / ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1);
    }
    const q = p - 0.5,
        r = q * q;
    return ((((((a[0] * r + a[1]) * r + a[2]) * r + a[3]) * r + a[4]) * r + a[5]) * q) / (((((b[0] * r + b[1]) * r + b[2]) * r + b[3]) * r + b[4]) * r + 1);
}
